import React, { useCallback, useEffect, useState } from "react";
import baseUrl from "../../../../api/baseUrl";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BottomTab from "../../../../components/pagination/BottomTab";

const LIMIT = 20;

function Completed() {
  const search = useSelector((state: RootState) => state.searching.patientData);
  const [payment, setpayment] = useState([]);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();

  const getData = useCallback(
    (searchTerm: string, pageNum: number) => {
      baseUrl
        .get("payment/getCompletedPayment", {
          headers: {
            keyword: searchTerm || "",
            page: pageNum,
            limit: LIMIT,
          },
        })
        .then((response) => {
          setpayment(response.data.data || []);
          setCount(response.data.count || 0);
        })
        .catch((error) => console.log(error));
    },
    []
  );

  useEffect(() => {
    setPage(1); // Reset to first page when search changes
  }, [search]);

  useEffect(() => {
    const activeSearchTerm = search || "";
    getData(activeSearchTerm, page);
  }, [search, page, getData]);

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center">
          <CheckCircleIcon className="text-primary" sx={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">Completed Payments</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">{count} records</p>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-600">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Invoice</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Patient</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Total</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Discount</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Card</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Cash</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Bank</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">paid</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
            {payment.map((item: any, index) => (
              <tr
                key={index}
                onClick={() => navigate(`/client/payments/invoice/${item?._id}`)}
                className="hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
              >
                <td className="px-3 py-2 text-xs font-medium text-indigo-600 dark:text-indigo-400">{item?.invoiceNo}</td>
                <td className="px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-200">{item?.patientData?.fullName}</td>
                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400">{item?.total}</td>
                <td className="px-3 py-2 text-xs text-orange-600 dark:text-orange-400">{item?.discount}</td>
                <td className="px-3 py-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400">{item?.card}</td>
                <td className="px-3 py-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400">{item?.cash}</td>
                <td className="px-3 py-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400">{item?.bankTransfer}</td>
                <td className="px-3 py-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400">{item?.paid}</td>
                <td className="px-3 py-2">
                  <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded-md text-xs font-semibold flex items-center gap-1 w-fit">
                    <CheckCircleIcon sx={{ fontSize: 12 }} /> Paid
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">{item?.patientData?.updatedAt?.split("T")[0]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-2">
        {payment.map((item: any, index) => (
          <div
            key={index}
            onClick={() => navigate(`/client/payments/invoice/${item?._id}`)}
            className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-100 dark:border-slate-700 p-3 cursor-pointer"
          >
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-medium text-xs text-indigo-600 dark:text-indigo-400">{item?.invoiceNo}</span>
              <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded-md text-xs font-semibold">Paid</span>
            </div>
            <h3 className="font-semibold text-xs text-slate-800 dark:text-slate-200">{item?.patientData?.fullName}</h3>
            <div className="flex items-center justify-between mt-1.5 text-xs">
              <span className="text-slate-500 dark:text-slate-400">Total: {item?.total}</span>
              <span className="font-semibold text-emerald-600 dark:text-emerald-400">{item?.card + item?.cash}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {count > LIMIT && (
        <BottomTab title="Payment" count={count} onPageChange={onPageChange} />
      )}
    </div>
  );
}

export default Completed;
